import { useEffect, useMemo, useState } from 'react';
import { Loader2, Workflow, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import type { AutomationRow } from './types';

interface EnrollSubscriberModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriberId: string;
  /** Automation ids the subscriber is currently actively enrolled in -- filtered out. */
  excludeAutomationIds: string[];
  onSuccess: () => void;
}

interface AutomationOption extends AutomationRow {
  step_count: number;
}

export default function EnrollSubscriberModal({
  isOpen,
  onClose,
  subscriberId,
  excludeAutomationIds,
  onSuccess,
}: EnrollSubscriberModalProps) {
  const [automations, setAutomations] = useState<AutomationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null);
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('automations')
          .select('*')
          .eq('status', 'active')
          .order('name');
        if (error) throw error;

        const ids = (data ?? []).map((a) => a.id as string);
        let counts = new Map<string, number>();
        if (ids.length > 0) {
          const stepsRes = await supabase
            .from('automation_steps')
            .select('automation_id')
            .in('automation_id', ids);
          if (stepsRes.error) throw stepsRes.error;
          counts = new Map<string, number>();
          for (const r of (stepsRes.data ?? []) as Array<{ automation_id: string }>) {
            counts.set(r.automation_id, (counts.get(r.automation_id) ?? 0) + 1);
          }
        }
        if (cancelled) return;
        setAutomations(
          (data ?? []).map((a) => ({
            ...(a as AutomationRow),
            step_count: counts.get(a.id as string) ?? 0,
          })),
        );
      } catch (e: unknown) {
        if (cancelled) return;
        toast.error(e instanceof Error ? e.message : 'Failed to load automations');
        setAutomations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !enrolling) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, enrolling]);

  const visible = useMemo(() => {
    const excluded = new Set(excludeAutomationIds);
    return automations.filter((a) => !excluded.has(a.id));
  }, [automations, excludeAutomationIds]);

  const handleEnroll = async () => {
    if (!selectedId) {
      toast.error('Pick an automation first');
      return;
    }
    if (!subscriberId) {
      toast.error('Missing subscriber id');
      return;
    }
    setEnrolling(true);
    try {
      const { error } = await supabase.rpc('enroll_subscriber_in_automation', {
        p_subscriber_id: subscriberId,
        p_automation_id: selectedId,
      });
      if (error) throw error;
      toast.success('Subscriber enrolled');
      onSuccess();
      onClose();
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : 'Enrollment failed';
      // The RPC raises symbolic codes; map them to friendlier messages.
      const friendly =
        raw.includes('automation_not_active')
          ? 'That automation is not active. Activate it first.'
          : raw.includes('subscriber_already_enrolled')
            ? 'Subscriber is already actively enrolled in this automation.'
            : raw.includes('automation_not_found')
              ? 'Automation no longer exists.'
              : raw;
      toast.error(friendly);
    } finally {
      setEnrolling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => {
        if (!enrolling) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Workflow className="w-5 h-5 text-emerald-500" />
            Add to automation
          </h2>
          <button
            onClick={onClose}
            disabled={enrolling}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : visible.length === 0 ? (
            <p className="py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
              {automations.length === 0
                ? 'No active automations. Create one and mark it active to enroll subscribers.'
                : 'Subscriber is already enrolled in every active automation.'}
            </p>
          ) : (
            <ul className="space-y-2">
              {visible.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(a.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedId === a.id
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/30'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {a.name}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {a.step_count} step{a.step_count === 1 ? '' : 's'} · {a.tz}
                      </span>
                    </div>
                    {a.description ? (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                        {a.description}
                      </div>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={enrolling}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleEnroll()}
            disabled={enrolling || !selectedId}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {enrolling ? 'Enrolling…' : 'Enroll subscriber'}
          </button>
        </div>
      </div>
    </div>
  );
}
